// Room director: the per-room state machine.
//   intro (banner) → combat (locked, waves, optional challenge) → cleared (reward,
//   exit gate) → exiting.  Repair / station rooms skip straight to cleared; boss
//   rooms spawn the sector boss and wait for boss:defeated + the major upgrade.
// Every call into another system happens from start(), update() or a handler.

import { SCENES, TILE } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { GameContext, RoomState, StationSceneData, Vec } from '../../core/contracts';
import { Subscriptions } from '../../core/events';
import type { SectorDef } from '../../core/sector';
import type { EquipInstance, ItemInstance, OrbType, RouteNode, ShopOffer } from '../../core/types';
import { ChallengeTracker, rollChallenge } from './challenges';
import { Crate } from './crate';
import { advanceThreshold, blitzLimit, isCombatRoom, planEncounter, type WavePlan } from './encounter';
import { EdgeDoors, ExitGate, Kiosk, SealedOrb, Terminal } from './props';
import type { RoomLayout } from './template';
import type { RoomMap } from './tilemap';

const INTRO_SECONDS = 0.8;
/** Seconds between spawns inside one wave (readability). */
const SPAWN_STAGGER = 0.14;
const PORTAL_MS = 700;
/** A wave arrives anyway after this long. */
const WAVE_TIMEOUT = 24;
/** Minimum time a wave stays before the next can trigger (portals must open first). */
const WAVE_MIN_TIME = 2.2;
const NEXT_WAVE_DELAY = 0.7;
/** Spawn points closer than this to the player are avoided. */
const SAFE_SPAWN_DIST = 400;
const BOSS_SPAWN_DELAY = 1.2;
const REPAIR_FRACTION = 0.4;
const STATION_REPAIR_FRACTION = 0.35;
/** Orb weights for combat room rewards. */
const ROOM_ORB_WEIGHTS: Record<OrbType, number> = { arsenal: 40, armor: 45, unstable: 15 };
const FIRST_ORB_FLAG = 'world:firstOrbGiven';

export interface RoomParts {
  node: RouteNode;
  sector: SectorDef;
  layout: RoomLayout;
  map: RoomMap;
  /** Ground spawn points (feet / surface positions). */
  ground: Vec[];
  /** Air spawn points (centres). */
  air: Vec[];
  exit: ExitGate;
  doors: EdgeDoors;
}

interface PendingSpawn {
  at: number;
  run: () => void;
}

export class RoomDirector {
  state: RoomState = 'intro';
  challenge: ChallengeTracker | null = null;
  locked = false;

  private subs = new Subscriptions();
  private removers: (() => void)[] = [];
  private t = 0;
  private waves: WavePlan[] = [];
  private waveIndex = -1;
  private waveStartedAt = 0;
  private nextWaveAt = -1;
  private pending: PendingSpawn[] = [];
  private combatStartedAt = 0;
  private damageTaken = 0;
  private bossSpawnAt = -1;
  private bossDown = false;
  private crates: Crate[] = [];
  private terminal: Terminal | null = null;
  private kiosk: Kiosk | null = null;
  private sealed: SealedOrb[] = [];
  private stationOffers: ShopOffer[] | null = null;
  private stationData: StationSceneData | null = null;
  private repairUsed = false;
  private destroyed = false;
  private readonly isBoss: boolean;
  private readonly fights: boolean;

  constructor(
    private ctx: GameContext,
    private parts: RoomParts,
  ) {
    const { node, layout, sector } = parts;
    this.isBoss = node.type === 'boss';
    this.fights = isCombatRoom(node.type);
    const feet = (m: { cx: number; ty: number }) => ({ x: m.cx * TILE, y: (m.ty + 1) * TILE });

    // Props that exist from the first frame (own game objects only).
    const t = layout.markers.T[0];
    if (t) this.terminal = new Terminal(ctx.scene, feet(t).x, feet(t).y);
    const s = layout.markers.S[0];
    if (node.type === 'station' && s) this.kiosk = new Kiosk(ctx.scene, 'station', feet(s).x, feet(s).y, COLORS.orbArsenal);
    const r = layout.markers.R[0];
    if (node.type === 'repair' && r) this.kiosk = new Kiosk(ctx.scene, 'repair', feet(r).x, feet(r).y, COLORS.heal);
    if (node.type === 'loot') {
      const types = this.lootRoomOrbTypes();
      layout.markers.O.forEach((m, i) => {
        if (i < types.length) this.sealed.push(new SealedOrb(ctx.scene, types[i], feet(m).x, feet(m).y));
      });
    }
    for (const c of layout.markers.C) this.crates.push(new Crate(ctx, feet(c).x, feet(c).y));
    parts.exit.setTitle(this.exitPrompt());
    void sector;
  }

  // ───────────────────────────── Setup ─────────────────────────────

  /** Two different orb types (one with the scarcity challenge option). Seeded by the loot stream. */
  private lootRoomOrbTypes(): OrbType[] {
    const rng = this.ctx.run.rng.loot.fork(`loot-room-${this.parts.node.id}`);
    const first = rng.weighted<OrbType>(['arsenal', 'armor', 'unstable'], (t) => (t === 'unstable' ? 0.6 : 1))!;
    const second = rng.weighted<OrbType>(['arsenal', 'armor', 'unstable'], (t) => (t === first ? 0 : t === 'unstable' ? 0.6 : 1))!;
    return this.ctx.run.hasChallenge('scarcity') ? [first] : [first, second];
  }

  private exitPrompt(): string {
    if (!this.isBoss) return 'ENTER ROUTE';
    return this.ctx.run.isFinalSector ? 'EXTRACT' : 'ASCEND';
  }

  start(): void {
    const { ctx, parts } = this;
    const { node, sector } = parts;

    for (const c of this.crates) ctx.enemies.register(c);
    this.removers.push(
      ctx.interact.add({
        x: parts.exit.x,
        y: parts.exit.y + 40,
        radius: 120,
        prompt: this.exitPrompt(),
        priority: 1,
        enabled: () => parts.exit.active && this.state === 'cleared',
        onInteract: () => this.requestExit(),
      }),
    );
    if (this.kiosk && node.type === 'repair') this.wireRepair(this.kiosk);
    if (this.kiosk && node.type === 'station') this.wireStation(this.kiosk);

    this.subs.add(
      ctx.bus.on('player:damaged', (e) => {
        if (this.state === 'combat') this.damageTaken += e.amount;
      }),
    );
    if (this.isBoss) this.subs.add(ctx.bus.on('boss:defeated', () => this.onBossDefeated()));
    this.subs.add(
      ctx.bus.on('room:exitCancelled', () => {
        if (this.state === 'exiting') this.state = 'cleared';
      }),
    );

    if (this.fights) {
      this.waves = planEncounter(ctx.rng, {
        type: node.type,
        difficulty: node.difficulty,
        sector,
        eliteSurge: ctx.run.hasChallenge('elite_surge'),
      });
      const hasElectric = (ctx.run.inventory.tagCounts(ctx.run.mech.kitTags).get('electric') ?? 0) > 0;
      const id = rollChallenge(ctx.rng, node.type, hasElectric);
      if (id) {
        this.challenge = new ChallengeTracker(ctx, id, {
          limit: blitzLimit(this.waves),
          rewardAt: () => this.rewardSpot(),
          onResolved: () => this.refreshTerminal(),
        });
      }
      this.lock();
    } else if (this.isBoss) {
      this.lock();
    } else {
      this.unlock();
    }
    this.refreshTerminal();

    // Intro banner: the sector card on its first room, the room name otherwise.
    if (node.type === 'start') ctx.bus.emit('ui:banner', { title: sector.name, subtitle: sector.subtitle, color: sector.palette.accent });
    else if (this.isBoss) ctx.bus.emit('ui:banner', { title: 'HOSTILE SIGNATURE', subtitle: `${sector.name} // GUARDIAN DETECTED`, color: COLORS.threat });
    else ctx.bus.emit('ui:banner', { title: parts.layout.name, subtitle: this.roomSubtitle(), color: node.type === 'elite' ? COLORS.threatHot : sector.palette.accent });
  }

  private roomSubtitle(): string {
    switch (this.parts.node.type) {
      case 'elite':
        return 'ELITE SIGNATURE DETECTED';
      case 'loot':
        return 'SUPPLY CACHE // GUARDED';
      case 'repair':
        return 'SAFE ZONE // REPAIR BAY ONLINE';
      case 'station':
        return 'SAFE ZONE // SERVICE STATION';
      default:
        return 'HOSTILES DETECTED';
    }
  }

  // ───────────────────────────── Frame ─────────────────────────────

  update(dt: number): void {
    if (this.destroyed) return;
    this.t += dt;
    this.parts.exit.update(dt);
    this.terminal?.update(dt);
    this.kiosk?.update(dt);
    for (const s of this.sealed) s.update(dt);

    if (this.state === 'intro' && this.t >= INTRO_SECONDS) {
      if (this.fights) this.beginCombat();
      else if (this.isBoss) {
        this.state = 'combat';
        this.bossSpawnAt = this.t + BOSS_SPAWN_DELAY - INTRO_SECONDS;
        this.ctx.bus.emit('room:combatStart', {});
      } else this.state = 'cleared';
    }

    // Staggered spawns.
    if (this.pending.length > 0) {
      for (let i = 0; i < this.pending.length; ) {
        if (this.pending[i].at <= this.t) {
          const p = this.pending.splice(i, 1)[0];
          p.run();
        } else i++;
      }
    }

    if (this.state === 'combat') {
      if (this.isBoss) this.updateBoss();
      else this.updateWaves();
      if (this.challenge) {
        this.challenge.update(dt);
        if (this.challenge.state.timeLeft !== undefined) this.refreshTerminal();
      }
    }
  }

  // ───────────────────────────── Combat ─────────────────────────────

  private beginCombat(): void {
    this.state = 'combat';
    this.combatStartedAt = this.ctx.time.now;
    this.ctx.bus.emit('room:combatStart', {});
    this.challenge?.start();
    this.refreshTerminal();
    this.spawnWave(0);
  }

  private updateWaves(): void {
    const alive = this.ctx.enemies.aliveCount();
    const last = this.waveIndex >= this.waves.length - 1;
    const waveAge = this.t - this.waveStartedAt;
    const stillSpawning = this.pending.length > 0;

    if (!last && this.nextWaveAt < 0 && !stillSpawning) {
      const wave = this.waves[this.waveIndex];
      const thinned = waveAge >= WAVE_MIN_TIME && alive <= advanceThreshold(wave);
      if (thinned || waveAge >= WAVE_TIMEOUT) this.nextWaveAt = this.t + NEXT_WAVE_DELAY;
    }
    if (this.nextWaveAt >= 0 && this.t >= this.nextWaveAt) {
      this.nextWaveAt = -1;
      this.spawnWave(this.waveIndex + 1);
      return;
    }
    if (last && !stillSpawning && waveAge >= 1 && alive === 0) this.clearRoom();
  }

  private spawnWave(index: number): void {
    const { ctx, parts } = this;
    this.waveIndex = index;
    this.waveStartedAt = this.t;
    const wave = this.waves[index];
    if (!wave) return;
    ctx.bus.emit('room:waveStart', { wave: index + 1, total: this.waves.length });
    const usedGround = new Set<Vec>();
    const usedAir = new Set<Vec>();
    wave.spawns.forEach((s, i) => {
      const point = this.pickSpawn(s.air ? parts.air : parts.ground, s.air ? usedAir : usedGround);
      if (!point) return;
      // Ground enemies appear a little above their surface and settle onto it.
      const x = point.x;
      const y = s.air ? point.y : point.y - 40;
      this.pending.push({
        at: this.t + i * SPAWN_STAGGER,
        run: () => {
          try {
            ctx.enemies.spawn(s.id, x, y, {
              elite: s.elite.length > 0 ? s.elite : undefined,
              portalMs: PORTAL_MS,
              level: parts.node.depth,
            });
          } catch (err) {
            console.error('[world] enemy spawn failed', s.id, err);
          }
        },
      });
    });
  }

  /** Random point at least SAFE_SPAWN_DIST from the player (falls back to the farthest ones). */
  private pickSpawn(points: readonly Vec[], used: Set<Vec>): Vec | null {
    const list = points.length > 0 ? points : this.parts.ground;
    if (list.length === 0) return null;
    const p = this.ctx.player;
    const free = list.filter((v) => !used.has(v));
    const pool = free.length > 0 ? free : list;
    const far = pool.filter((v) => Math.hypot(v.x - p.x, v.y - p.y) >= SAFE_SPAWN_DIST);
    let pick: Vec;
    if (far.length > 0) pick = this.ctx.rng.pick(far);
    else {
      const sorted = [...pool].sort((a, b) => Math.hypot(b.x - p.x, b.y - p.y) - Math.hypot(a.x - p.x, a.y - p.y));
      pick = this.ctx.rng.pick(sorted.slice(0, Math.min(2, sorted.length)));
    }
    used.add(pick);
    return pick;
  }

  // ───────────────────────────── Boss ─────────────────────────────

  private updateBoss(): void {
    if (this.bossSpawnAt >= 0 && this.t >= this.bossSpawnAt) {
      this.bossSpawnAt = -1;
      const b = this.parts.layout.markers.B[0];
      const x = b ? b.cx * TILE : this.parts.map.width / 2;
      const y = b ? (b.ty + 1) * TILE : this.parts.map.height - 2 * TILE;
      this.combatStartedAt = this.ctx.time.now;
      try {
        this.ctx.bosses.spawn(this.parts.sector.boss, x, y);
      } catch (err) {
        // Never trap the player in a room without a boss.
        console.error('[world] boss spawn failed', err);
        void this.onBossDefeated();
      }
    }
  }

  private async onBossDefeated(): Promise<void> {
    if (this.bossDown || this.destroyed) return;
    this.bossDown = true;
    const { ctx, parts } = this;
    this.state = 'cleared';
    ctx.run.stats.roomsCleared++;
    ctx.bus.emit('room:cleared', { node: parts.node, timeSec: ctx.time.now - this.combatStartedAt, damageTaken: Math.round(this.damageTaken) });
    // Let the destruction sequence play before the pausing upgrade choice.
    await this.wait(1.8);
    if (this.destroyed) return;
    try {
      await ctx.loot.offerMajorUpgrade();
    } catch (err) {
      console.error('[world] major upgrade offer failed', err);
    }
    if (this.destroyed) return;
    ctx.bus.emit('ui:banner', {
      title: ctx.run.isFinalSector ? 'SECTOR SECURED' : 'GUARDIAN DOWN',
      subtitle: ctx.run.isFinalSector ? 'EXTRACTION GATE ONLINE' : 'ASCENSION GATE ONLINE',
      color: COLORS.uiGood,
    });
    this.unlock();
  }

  // ───────────────────────────── Clear & rewards ─────────────────────────────

  private clearRoom(): void {
    if (this.state !== 'combat') return;
    const { ctx, parts } = this;
    this.state = 'cleared';
    ctx.run.stats.roomsCleared++;
    ctx.bus.emit('room:cleared', {
      node: parts.node,
      timeSec: Math.round((ctx.time.now - this.combatStartedAt) * 10) / 10,
      damageTaken: Math.round(this.damageTaken),
    });
    ctx.audio.play('room_clear');
    ctx.bus.emit('ui:banner', { title: 'AREA CLEAR', subtitle: 'ROUTE UNLOCKED', color: COLORS.uiGood });
    this.challenge?.onRoomCleared();
    this.refreshTerminal();
    void this.grantReward();
  }

  private async grantReward(): Promise<void> {
    const { ctx, parts } = this;
    const spot = this.rewardSpot();
    switch (parts.node.type) {
      case 'start':
        this.dropOrb('arsenal', spot);
        this.unlock();
        break;
      case 'combat': {
        const scrap = 10 + parts.node.depth * 4 + ctx.run.sectorIndex * 6;
        ctx.loot.spawnScrap(spot.x, spot.y - 30, scrap);
        const type = ctx.run.flags.has(FIRST_ORB_FLAG)
          ? ctx.rng.weighted<OrbType>(['arsenal', 'armor', 'unstable'], (t) => ROOM_ORB_WEIGHTS[t])!
          : 'arsenal';
        this.dropOrb(type, spot);
        this.unlock();
        break;
      }
      case 'loot':
        for (const s of this.sealed) {
          const at = s.release();
          try {
            ctx.fx.ring(at.x, at.y, 90, { color: COLORS.threat, duration: 380 });
            ctx.fx.debris(at.x, at.y, { count: 10, color: COLORS.threatGlow, speed: 300 });
          } catch {
            // Cosmetic only.
          }
          ctx.loot.spawnOrb(s.type, at.x, at.y, { source: 'loot_room' });
        }
        ctx.run.flags.add(FIRST_ORB_FLAG);
        this.unlock();
        break;
      case 'elite':
        await this.wait(0.9);
        if (this.destroyed) return;
        try {
          await ctx.loot.offerChoice(ctx.loot.rollChoices('mixed', 'elite', 3), 'ELITE CACHE', {
            subtitle: 'CHOOSE ONE MODULE',
          });
        } catch (err) {
          console.error('[world] elite cache failed', err);
        }
        if (this.destroyed) return;
        ctx.run.flags.add(FIRST_ORB_FLAG);
        this.unlock();
        break;
      default:
        this.unlock();
    }
  }

  private dropOrb(type: OrbType, at: Vec): void {
    this.ctx.run.flags.add(FIRST_ORB_FLAG);
    this.ctx.loot.spawnOrb(type, at.x, at.y - 90, { source: 'room', dropIn: true });
  }

  /** Ground point nearest the room centre (reachable surface), as feet position. */
  private rewardSpot(): Vec {
    const { map, ground } = this.parts;
    const cx = map.width / 2;
    if (ground.length === 0) return { x: this.ctx.player.x, y: this.ctx.player.feetY };
    let best = ground[0];
    let bestD = Infinity;
    for (const g of ground) {
      // Prefer the main floor slightly over high perches.
      const d = Math.abs(g.x - cx) + (map.height - g.y) * 0.35;
      if (d < bestD) {
        bestD = d;
        best = g;
      }
    }
    return best;
  }

  // ───────────────────────────── Exit / lock ─────────────────────────────

  lock(): void {
    this.locked = true;
    this.parts.exit.setActive(false);
    this.parts.doors.setLocked(true);
  }

  unlock(): void {
    const wasLocked = this.locked;
    this.locked = false;
    this.parts.doors.setLocked(false);
    if (!this.parts.exit.active) {
      this.parts.exit.setActive(true);
      if (wasLocked) this.ctx.audio.play('door_unlock');
    }
  }

  private requestExit(): void {
    if (this.state !== 'cleared' || !this.parts.exit.active) return;
    this.state = 'exiting';
    this.ctx.bus.emit('room:exitRequested', {});
  }

  // ───────────────────────────── Terminal ─────────────────────────────

  private refreshTerminal(): void {
    if (!this.terminal) return;
    const c = this.challenge;
    if (!c || this.state === 'intro') this.terminal.show(null, '', '');
    else this.terminal.show(c.state, c.name, c.rewardText);
  }

  // ───────────────────────────── Repair bay ─────────────────────────────

  private wireRepair(kiosk: Kiosk): void {
    const { ctx } = this;
    const noRepairs = ctx.run.hasChallenge('no_repairs');
    this.removers.push(
      ctx.interact.add({
        x: kiosk.x,
        y: kiosk.y,
        radius: 130,
        prompt: noRepairs ? 'SALVAGE PARTS +40 SCRAP' : `REPAIR +${Math.round(REPAIR_FRACTION * 100)}% HP`,
        priority: 5,
        enabled: () => !this.repairUsed,
        onInteract: () => {
          if (this.repairUsed) return;
          this.repairUsed = true;
          kiosk.setSpent(true);
          if (noRepairs) {
            ctx.loot.spawnScrap(kiosk.x, kiosk.y, 40);
          } else {
            ctx.player.heal(ctx.player.stats.maxHp * REPAIR_FRACTION);
            ctx.audio.play('heal');
            try {
              ctx.fx.ring(ctx.player.x, ctx.player.y, 120, { color: COLORS.heal, duration: 500 });
              ctx.fx.floatText(ctx.player.x, ctx.player.y - 80, 'HULL RESTORED', { color: COLORS.heal });
            } catch {
              // Cosmetic only.
            }
          }
        },
      }),
    );
  }

  // ───────────────────────────── Service station ─────────────────────────────

  private wireStation(kiosk: Kiosk): void {
    this.removers.push(
      this.ctx.interact.add({
        x: kiosk.x,
        y: kiosk.y,
        radius: 150,
        prompt: 'SERVICE STATION',
        priority: 5,
        onInteract: () => this.openStation(kiosk),
      }),
    );
  }

  private openStation(kiosk: Kiosk): void {
    const { ctx } = this;
    const run = ctx.run;
    const noRepairs = run.hasChallenge('no_repairs');
    this.stationOffers ??= ctx.loot.rollShop(4);
    const data: StationSceneData = (this.stationData ??= {
      offers: this.stationOffers,
      repairCost: noRepairs ? 0 : 30 + 15 * run.sectorIndex,
      rerollCost: 25,
      onBuy: (offer) => this.buy(offer, kiosk),
      onRepair: () => {
        if (noRepairs) return;
        if (!run.spendScrap(data.repairCost)) return;
        ctx.player.heal(ctx.player.stats.maxHp * STATION_REPAIR_FRACTION);
        ctx.audio.play('heal');
      },
      onReroll: (item) => {
        if (!run.spendScrap(data.rerollCost)) return;
        if (this.reroll(item)) data.rerollCost = Math.round(data.rerollCost * 1.5);
        else run.addScrap(data.rerollCost); // refund: nothing to reroll into
      },
      onClose: () => {
        if (!this.destroyed) ctx.scene.scene.resume();
      },
    });
    ctx.scene.scene.pause();
    ctx.scene.scene.launch(SCENES.STATION, data);
    ctx.scene.scene.bringToTop(SCENES.STATION);
  }

  private buy(offer: ShopOffer, kiosk: Kiosk): void {
    const { ctx } = this;
    if (offer.sold || !ctx.run.spendScrap(offer.price)) return;
    offer.sold = true;
    const inv = ctx.run.inventory;
    if (inv.canAutoEquip(offer.item)) {
      inv.equip(offer.item);
      inv.changed();
    } else {
      ctx.loot.spawnItemPickup(offer.item, kiosk.x + 150, kiosk.y + 40, { pop: true });
    }
  }

  /** Replace an equipped item with a fresh roll of the same kind (and slot). */
  private reroll(item: ItemInstance): boolean {
    const { ctx } = this;
    const inv = ctx.run.inventory;
    if (item.kind === 'mutation') return false;
    const type: OrbType = item.kind === 'equipment' ? 'armor' : 'arsenal';
    const matches = (c: ItemInstance) =>
      c.kind === item.kind && c.defId !== item.defId && (item.kind !== 'equipment' || (c as EquipInstance).slot === item.slot);
    let pick: ItemInstance | null = null;
    for (let i = 0; i < 3 && !pick; i++) pick = ctx.loot.rollChoices(type, 'shop', 6).find(matches) ?? null;
    if (!pick) return false;
    if (pick.kind === 'weapon') {
      const slot = inv.weapons.findIndex((w) => w?.uid === item.uid);
      inv.equip(pick, { weaponSlot: Math.max(0, slot) });
    } else if (pick.kind === 'mod') {
      let done = false;
      inv.weapons.forEach((w, slot) => {
        const idx = w?.mods.findIndex((m) => m.uid === item.uid) ?? -1;
        if (!done && idx >= 0) {
          inv.equip(pick!, { weaponSlot: slot, modIndex: idx });
          done = true;
        }
      });
      if (!done) return false;
    } else inv.equip(pick);
    inv.changed();
    ctx.bus.emit('ui:toast', { text: 'REROLL COMPLETE', color: COLORS.uiGood });
    return true;
  }

  // ───────────────────────────── Utils ─────────────────────────────

  /** Gameplay-time wait (pauses with the scene). */
  private wait(seconds: number): Promise<void> {
    return new Promise((resolve) => this.pending.push({ at: this.t + seconds, run: resolve }));
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.subs.dispose();
    for (const r of this.removers) {
      try {
        r();
      } catch {
        // Interact system already gone.
      }
    }
    this.removers = [];
    this.pending = [];
    this.challenge?.destroy();
    for (const c of this.crates) c.destroyObject();
    this.crates = [];
    this.terminal?.destroy();
    this.kiosk?.destroy();
    for (const s of this.sealed) s.destroy();
    this.sealed = [];
  }
}
